import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import ClienteCombobox from "@/components/ClienteCombobox";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, Zap, Settings2, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

export function ImportPeticionDialog({ open, onOpenChange, onCreated }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState(1); // 1: upload, 2: configure & preview, 3: success
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  // Profiles
  const [perfiles, setPerfiles] = useState([]);
  const [selectedPerfilId, setSelectedPerfilId] = useState(null);
  const [showManageProfiles, setShowManageProfiles] = useState(false);
  // Step 2 state
  const [clientes, setClientes] = useState([]);
  const [comerciales, setComerciales] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [form, setForm] = useState({
    cliente_id: "",
    nombre_peticion: "",
    marca: "",
    fecha_objetivo: "",
    comercial_id: "",
    tipo_linea: "solo_montaje",
    lleva_retirada: false,
    material_global_id: "",
  });
  const [items, setItems] = useState([]);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/clientes").then((r) => setClientes(r.data || []));
    api.get("/users?roles=admin,comercial").then((r) => setComerciales(r.data || [])).catch(() => {});
    api.get("/materiales").then((r) => setMateriales(r.data || [])).catch(() => {});
    loadPerfiles();
  }, [open]);

  const loadPerfiles = () => {
    api.get("/perfiles-import-peticion?activo=true").then((r) => setPerfiles(r.data || [])).catch(() => {});
  };

  const reset = () => {
    setStep(1); setPreview(null); setItems([]); setResult(null);
    setSelectedPerfilId(null);
    setForm({ cliente_id: "", nombre_peticion: "", marca: "", fecha_objetivo: "",
              comercial_id: "", tipo_linea: "solo_montaje", lleva_retirada: false, material_global_id: "" });
    if (fileRef.current) fileRef.current.value = "";
  };

  // Lanza el selector de archivo asociado a un perfil concreto (o ninguno)
  const triggerFileSelect = (perfilId) => {
    setSelectedPerfilId(perfilId || null);
    // dar un tick para que el state se aplique antes del click programático
    setTimeout(() => fileRef.current?.click(), 0);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (selectedPerfilId) fd.append("perfil_id", selectedPerfilId);
      const { data } = await api.post("/peticiones/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const its = data.items.map((it) => ({ ...it, _accion: "crear" }));
      setPreview(data);
      setItems(its);
      // Pre-rellenar el formulario con defaults del perfil (si los hay)
      const pd = data.perfil_defaults || {};
      setForm((f) => ({
        ...f,
        cliente_id: pd.cliente_id || "",
        nombre_peticion: data.titulo_detectado || pd.perfil_nombre || "Campaña sin título",
        marca: pd.marca || "",
        comercial_id: pd.comercial_id || "",
        tipo_linea: pd.tipo_linea || "solo_montaje",
        lleva_retirada: !!pd.lleva_retirada,
        material_global_id: pd.material_global_id || "",
      }));
      if (data.n_descartados > 0) {
        toast.info(`Perfil aplicado: ${data.n_descartados} fila(s) descartada(s) por filtros`);
      }
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo analizar el Excel");
    } finally {
      setLoading(false);
      // limpiar input para permitir re-subir el mismo archivo
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setItem = (idx, patch) => {
    setItems((arr) => arr.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };
  const bulkMaterial = (matId) => {
    setItems((arr) => arr.map((it) => ({ ...it, _material_match_id: matId })));
  };

  const commit = async () => {
    if (!form.cliente_id) return toast.error("Selecciona el cliente DNF que paga la campaña");
    if (!form.nombre_peticion.trim()) return toast.error("Indica un nombre para la petición");
    setCommitting(true);
    try {
      const payload = {
        ...form,
        items,
        comercial_id: form.comercial_id === "__none__" ? null : (form.comercial_id || null),
      };
      delete payload.material_global_id;
      const { data } = await api.post("/peticiones/import/commit", payload);
      setResult(data);
      setStep(3);
      toast.success(`✅ Petición ${data.peticion_codigo} creada con ${data.lineas_creadas} puntos`);
      onCreated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error en la importación");
    } finally { setCommitting(false); }
  };

  const totalImportar = items.filter((it) => it._accion !== "omitir").length;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-6xl max-h-[92vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-amber-400" /> Importar petición desde Excel del cliente
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Usa un perfil guardado para imports 1-click o sube un Excel y configura los campos manualmente.
          </DialogDescription>
        </DialogHeader>

        {/* Input file oculto, compartido por todos los disparadores */}
        <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onFile}
               className="hidden" id="pet-import-file" data-testid="pet-import-file" />

        {step === 1 && (
          <div className="space-y-4">
            {/* Perfiles 1-click */}
            {perfiles.length > 0 && (
              <div className="bg-amber-500/5 border border-amber-700/40 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-300 font-semibold">
                    <Zap className="w-4 h-4" /> Importación 1-click — Perfiles guardados
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setShowManageProfiles(true)}
                          className="text-zinc-400 hover:text-zinc-100 h-7"
                          data-testid="pet-import-manage-profiles">
                    <Settings2 className="w-3.5 h-3.5 mr-1" /> Gestionar
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {perfiles.map((p) => (
                    <Button key={p.id} onClick={() => triggerFileSelect(p.id)}
                            disabled={loading}
                            data-testid={`pet-import-perfil-${p.id}`}
                            className="bg-zinc-900 border border-zinc-700 hover:border-amber-500 hover:bg-zinc-900 text-zinc-100 font-medium h-auto py-2.5 px-3 flex flex-col items-start gap-0.5">
                      <span className="flex items-center gap-1.5">
                        {p.icon && <span>{p.icon}</span>}
                        <span>{p.nombre}</span>
                      </span>
                      <span className="text-[10px] text-zinc-500 font-normal">{p.cliente_nombre}</span>
                    </Button>
                  ))}
                </div>
                <div className="text-[11px] text-zinc-500 mt-3">
                  Los perfiles aplican filtros (ej. excluir filas «Completado»), material por defecto y cliente automáticamente.
                </div>
              </div>
            )}

            {/* Carga manual */}
            <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-zinc-800 rounded-lg">
              <Upload className="w-10 h-10 text-zinc-500 mb-3" />
              <p className="text-sm text-zinc-300 mb-1">
                {perfiles.length > 0 ? "O sube un Excel sin perfil (configuración manual)" : "Sube el Excel que te ha mandado el cliente"}
              </p>
              <p className="text-xs text-zinc-500 mb-4 text-center max-w-md">
                Detectaremos automáticamente: cabeceras, espacios, medidas, materiales y visuales. Acepta `.xlsx`.
              </p>
              <Button onClick={() => triggerFileSelect(null)} disabled={loading}
                      className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
                      data-testid="pet-import-manual-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando…</> : "Seleccionar Excel"}
              </Button>
              {perfiles.length === 0 && (
                <Button variant="ghost" size="sm" onClick={() => setShowManageProfiles(true)}
                        className="mt-3 text-amber-400 hover:text-amber-300"
                        data-testid="pet-import-create-first-profile">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Crea tu primer perfil para imports 1-click
                </Button>
              )}
            </div>
          </div>
        )}

        {step === 2 && preview && (
          <>
            <div className="bg-amber-500/5 border border-amber-700/40 rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-amber-300 uppercase tracking-wider font-semibold">
                  ⚙️ Configuración global de la campaña
                </div>
                {preview.perfil_defaults && (
                  <div className="text-[11px] text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded">
                    Perfil: <b>{preview.perfil_defaults.perfil_nombre}</b>
                    {preview.n_descartados > 0 && (
                      <span className="ml-2 text-zinc-400">· {preview.n_descartados} fila(s) filtradas</span>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Cliente DNF *</Label>
                  <ClienteCombobox
                    value={form.cliente_id}
                    onChange={(id) => setForm((f) => ({ ...f, cliente_id: id }))}
                    clientes={clientes}
                    placeholder="Quién paga..."
                    testId="pet-import-cliente"
                    className="bg-zinc-950 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Nombre petición *</Label>
                  <Input value={form.nombre_peticion} onChange={(e) => setForm({ ...form, nombre_peticion: e.target.value })}
                    className="bg-zinc-950 border-zinc-700 mt-1" data-testid="pet-import-nombre" />
                </div>
                <div>
                  <Label className="text-xs">Marca / campaña</Label>
                  <Input value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })}
                    className="bg-zinc-950 border-zinc-700 mt-1" placeholder="Ej. Anthelios" />
                </div>
                <div>
                  <Label className="text-xs">Fecha objetivo (entrega)</Label>
                  <Input type="date" value={form.fecha_objetivo}
                    onChange={(e) => setForm({ ...form, fecha_objetivo: e.target.value })}
                    className="bg-zinc-950 border-zinc-700 mt-1" />
                </div>

                <div>
                  <Label className="text-xs">Tipo de trabajo *</Label>
                  <Select value={form.tipo_linea} onValueChange={(v) => setForm({ ...form, tipo_linea: v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-700 mt-1" data-testid="pet-import-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                      <SelectItem value="solo_montaje">Solo montaje</SelectItem>
                      <SelectItem value="produccion_montaje">Producción + montaje</SelectItem>
                      <SelectItem value="diseno_produccion_montaje">Diseño + producción + montaje</SelectItem>
                      <SelectItem value="diseno_produccion">Diseño + producción (sin montaje)</SelectItem>
                      <SelectItem value="diseno">Solo diseño</SelectItem>
                      <SelectItem value="otros">Otros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Comercial asignado</Label>
                  <Select value={form.comercial_id} onValueChange={(v) => setForm({ ...form, comercial_id: v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-700 mt-1">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100 max-h-60">
                      <SelectItem value="__none__">—</SelectItem>
                      {comerciales.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Material por defecto</Label>
                  <Select value={form.material_global_id}
                    onValueChange={(v) => { setForm({ ...form, material_global_id: v }); bulkMaterial(v); }}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-700 mt-1" data-testid="pet-import-material-bulk">
                      <SelectValue placeholder="(usar adivinado por fila)" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100 max-h-72">
                      {materiales.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col">
                  <Label className="text-xs">Retirada de campaña anterior</Label>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input type="checkbox" checked={form.lleva_retirada}
                      onChange={(e) => setForm({ ...form, lleva_retirada: e.target.checked })}
                      data-testid="pet-import-retirada"
                      className="w-4 h-4 accent-amber-500" />
                    <span className="text-sm text-zinc-200">Generar retirada automática</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-1 text-sm">
              <div className="flex gap-4">
                <KPI label="Detectados" value={preview.n_items} />
                <KPI label="A importar" value={totalImportar} color="text-emerald-400" />
                {preview.n_descartados > 0 && (
                  <KPI label="Filtrados" value={preview.n_descartados} color="text-zinc-500" />
                )}
                <KPI label="Hoja" value={preview.sheet_name || "—"} />
              </div>
            </div>

            <div className="flex-1 overflow-auto border border-zinc-800 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-zinc-900 text-zinc-400 sticky top-0">
                  <tr>
                    <th className="text-left p-2 w-10">#</th>
                    <th className="text-left p-2">Espacio (PDV)</th>
                    <th className="text-left p-2 w-44">Visual</th>
                    <th className="text-left p-2 w-24">Dim cm</th>
                    <th className="text-left p-2 w-48">Material DNF</th>
                    <th className="text-left p-2 w-32">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} className="border-t border-zinc-800/60 hover:bg-zinc-900/30">
                      <td className="p-2 text-zinc-500">{it._row_idx}</td>
                      <td className="p-2">
                        <div className="font-medium text-zinc-100 truncate max-w-[260px]">{it.nombre}</div>
                        <div className="text-[10px] text-zinc-500 truncate max-w-[260px]">
                          {it.direccion}{it.cp ? `, ${it.cp}` : ""}{it.poblacion ? ` ${it.poblacion}` : ""}{it.provincia ? ` (${it.provincia})` : ""}
                        </div>
                        {it._espacio_match_id && (
                          <div className="text-[10px] text-emerald-400 flex items-center gap-1 mt-0.5">
                            <CheckCircle2 className="w-3 h-3" /> Match existente: {it._espacio_match_nombre}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-zinc-300 align-top">{it.visual || "—"}</td>
                      <td className="p-2 text-zinc-200 align-top font-mono whitespace-nowrap">
                        {it.ancho_cm && it.alto_cm ? `${it.ancho_cm}×${it.alto_cm}` : "—"}
                      </td>
                      <td className="p-2 align-top">
                        <Select value={it._material_match_id || ""}
                          onValueChange={(v) => setItem(idx, { _material_match_id: v })}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-700 h-7 text-xs">
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-zinc-700 max-h-60">
                            {materiales.map((m) => (
                              <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {it._material_guess_slug && !it._material_match_id && (
                          <div className="text-[10px] text-amber-400 flex items-center gap-1 mt-0.5">
                            <AlertTriangle className="w-3 h-3" /> Detectado: {it._material_guess_slug.replace("_", " ")}
                          </div>
                        )}
                      </td>
                      <td className="p-2 align-top">
                        <Select value={it._accion || "crear"} onValueChange={(v) => setItem(idx, { _accion: v })}>
                          <SelectTrigger className="bg-zinc-900 border-zinc-700 h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-zinc-700">
                            <SelectItem value="crear">Crear</SelectItem>
                            <SelectItem value="omitir">Omitir</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {step === 3 && result && (
          <div className="flex flex-col items-center text-center p-8">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mb-3" />
            <h2 className="text-xl font-bold mb-2">¡Petición creada!</h2>
            <p className="text-zinc-300 mb-4">
              <span className="text-amber-400 font-mono font-bold">{result.peticion_codigo}</span> · {result.peticion_nombre}
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm w-full max-w-md">
              <div className="bg-zinc-900 border border-zinc-800 rounded p-3">
                <div className="text-zinc-500 text-xs uppercase">Espacios</div>
                <div className="text-lg font-bold">
                  <span className="text-emerald-400">+{result.espacios_creados}</span>
                  {result.espacios_reusados > 0 && <span className="text-zinc-500 ml-2 text-sm">· {result.espacios_reusados} reusados</span>}
                </div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded p-3">
                <div className="text-zinc-500 text-xs uppercase">Líneas + Componentes</div>
                <div className="text-lg font-bold text-emerald-400">{result.lineas_creadas}</div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                  className="bg-transparent border-zinc-700">Cerrar</Button>
          {step === 2 && (
            <Button onClick={commit} disabled={committing || totalImportar === 0}
                    className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
                    data-testid="pet-import-commit">
              {committing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando…</>
                          : `Crear petición con ${totalImportar} punto${totalImportar === 1 ? "" : "s"}`}
            </Button>
          )}
          {step === 3 && (
            <Button onClick={() => { onOpenChange(false); reset(); }}
                    className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
              Hecho
            </Button>
          )}
        </DialogFooter>

        <ManageProfilesDialog open={showManageProfiles}
                              onOpenChange={setShowManageProfiles}
                              clientes={clientes}
                              materiales={materiales}
                              comerciales={comerciales}
                              onChanged={loadPerfiles} />
      </DialogContent>
    </Dialog>
  );
}

function KPI({ label, value, color = "text-zinc-100" }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-zinc-500 text-xs uppercase">{label}</span>
      <span className={`font-mono font-semibold ${color}`}>{value}</span>
    </div>
  );
}

// ============ ManageProfilesDialog ============
function ManageProfilesDialog({ open, onOpenChange, clientes, materiales, comerciales, onChanged }) {
  const [perfiles, setPerfiles] = useState([]);
  const [editing, setEditing] = useState(null); // null | {…} → formulario en pantalla
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get("/perfiles-import-peticion").then((r) => setPerfiles(r.data || [])).catch(() => {});
  };
  useEffect(() => { if (open) { load(); setEditing(null); } }, [open]);

  const blank = () => ({
    nombre: "", cliente_id: "", descripcion: "", icon: "",
    tipo_linea_default: "solo_montaje", lleva_retirada_default: false,
    marca_default: "", comercial_id_default: "", material_default_id: "",
    filtros: [], aliases_extra: null, orden: 0, activo: true,
  });

  const save = async () => {
    if (!editing.nombre.trim()) return toast.error("Indica un nombre para el perfil");
    if (!editing.cliente_id) return toast.error("Selecciona el cliente DNF");
    setLoading(true);
    try {
      const payload = { ...editing };
      // Limpiar campos vacíos opcionales
      ["marca_default", "comercial_id_default", "material_default_id", "descripcion", "icon"].forEach((k) => {
        if (payload[k] === "" || payload[k] === "__none__") payload[k] = null;
      });
      if (editing.id) {
        await api.patch(`/perfiles-import-peticion/${editing.id}`, payload);
        toast.success("Perfil actualizado");
      } else {
        await api.post("/perfiles-import-peticion", payload);
        toast.success("Perfil creado");
      }
      setEditing(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setLoading(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar el perfil "${p.nombre}"?`)) return;
    try {
      await api.delete(`/perfiles-import-peticion/${p.id}`);
      toast.success("Perfil eliminado");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  const addFiltro = () => {
    setEditing({ ...editing, filtros: [...(editing.filtros || []),
      { columna: "estado", operador: "not_contains_any", valores: ["completado", "completed", "hecho"] }] });
  };
  const removeFiltro = (idx) => {
    setEditing({ ...editing, filtros: editing.filtros.filter((_, i) => i !== idx) });
  };
  const setFiltro = (idx, patch) => {
    setEditing({ ...editing, filtros: editing.filtros.map((f, i) => i === idx ? { ...f, ...patch } : f) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-3xl max-h-[88vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-amber-400" />
            {editing ? (editing.id ? "Editar perfil" : "Nuevo perfil de importación") : "Perfiles de importación de peticiones"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            {editing
              ? "Configura los valores por defecto y filtros aplicados al importar Excels de este cliente."
              : "Gestiona los perfiles que aparecen como botones de import 1-click."}
          </DialogDescription>
        </DialogHeader>

        {!editing && (
          <>
            <div className="flex justify-end mb-3">
              <Button onClick={() => setEditing(blank())}
                      data-testid="perfil-import-new"
                      className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
                <Plus className="w-4 h-4 mr-1" /> Nuevo perfil
              </Button>
            </div>
            <div className="flex-1 overflow-auto border border-zinc-800 rounded-lg">
              {perfiles.length === 0 ? (
                <div className="p-8 text-center text-zinc-500 text-sm">
                  Aún no hay perfiles. Crea uno para automatizar los Excels recurrentes (Diagrafos, ONCE...).
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase">
                    <tr>
                      <th className="text-left p-2">Nombre</th>
                      <th className="text-left p-2">Cliente DNF</th>
                      <th className="text-left p-2">Material</th>
                      <th className="text-left p-2 w-24">Filtros</th>
                      <th className="text-left p-2 w-20">Activo</th>
                      <th className="text-right p-2 w-28">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perfiles.map((p) => {
                      const mat = materiales.find((m) => m.id === p.material_default_id);
                      return (
                        <tr key={p.id} className="border-t border-zinc-800/60 hover:bg-zinc-900/30">
                          <td className="p-2">
                            <div className="font-medium">{p.icon && <span className="mr-1">{p.icon}</span>}{p.nombre}</div>
                            {p.descripcion && <div className="text-[11px] text-zinc-500">{p.descripcion}</div>}
                          </td>
                          <td className="p-2 text-zinc-300">{p.cliente_nombre}</td>
                          <td className="p-2 text-zinc-300">{mat ? mat.nombre : "—"}</td>
                          <td className="p-2 text-zinc-300">{p.filtros?.length || 0}</td>
                          <td className="p-2">
                            <span className={p.activo ? "text-emerald-400" : "text-zinc-500"}>
                              {p.activo ? "Sí" : "No"}
                            </span>
                          </td>
                          <td className="p-2 text-right">
                            <Button size="sm" variant="ghost" onClick={() => setEditing(p)}
                                    data-testid={`perfil-import-edit-${p.id}`}
                                    className="text-amber-400 hover:text-amber-300 h-7">
                              Editar
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => remove(p)}
                                    data-testid={`perfil-import-delete-${p.id}`}
                                    className="text-rose-400 hover:text-rose-300 h-7 ml-1">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {editing && (
          <div className="flex-1 overflow-auto space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nombre del perfil *</Label>
                <Input value={editing.nombre} onChange={(e) => setEditing({ ...editing, nombre: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  placeholder="Ej. Diagrafos, ONCE Zedis…"
                  data-testid="perfil-import-nombre" />
              </div>
              <div>
                <Label className="text-xs">Icono (emoji opcional)</Label>
                <Input value={editing.icon || ""} onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 mt-1" placeholder="💊" maxLength={4} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Descripción</Label>
                <Input value={editing.descripcion || ""} onChange={(e) => setEditing({ ...editing, descripcion: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  placeholder="Ej. Campañas mensuales de farmacias con visual translúcido" />
              </div>
              <div>
                <Label className="text-xs">Cliente DNF *</Label>
                <ClienteCombobox
                  value={editing.cliente_id}
                  onChange={(id) => setEditing((e) => ({ ...e, cliente_id: id }))}
                  clientes={clientes}
                  placeholder="Selecciona…"
                  testId="perfil-import-cliente"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Tipo de trabajo</Label>
                <Select value={editing.tipo_linea_default} onValueChange={(v) => setEditing({ ...editing, tipo_linea_default: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                    <SelectItem value="solo_montaje">Solo montaje</SelectItem>
                    <SelectItem value="produccion_montaje">Producción + montaje</SelectItem>
                    <SelectItem value="diseno_produccion_montaje">Diseño + producción + montaje</SelectItem>
                    <SelectItem value="diseno_produccion">Diseño + producción (sin montaje)</SelectItem>
                    <SelectItem value="diseno">Solo diseño</SelectItem>
                    <SelectItem value="otros">Otros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Material por defecto</Label>
                <Select value={editing.material_default_id || ""}
                        onValueChange={(v) => setEditing({ ...editing, material_default_id: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100 max-h-72">
                    {materiales.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Comercial por defecto</Label>
                <Select value={editing.comercial_id_default || "__none__"}
                        onValueChange={(v) => setEditing({ ...editing, comercial_id_default: v === "__none__" ? null : v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100 max-h-60">
                    <SelectItem value="__none__">—</SelectItem>
                    {comerciales.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Marca por defecto</Label>
                <Input value={editing.marca_default || ""}
                       onChange={(e) => setEditing({ ...editing, marca_default: e.target.value })}
                       className="bg-zinc-900 border-zinc-800 mt-1"
                       placeholder="Ej. ONCE" />
              </div>
              <div className="flex items-center gap-4 md:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={editing.lleva_retirada_default}
                    onChange={(e) => setEditing({ ...editing, lleva_retirada_default: e.target.checked })}
                    className="w-4 h-4 accent-amber-500" />
                  Generar retirada automática
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={editing.activo}
                    onChange={(e) => setEditing({ ...editing, activo: e.target.checked })}
                    className="w-4 h-4 accent-amber-500" />
                  Perfil activo (visible en 1-click)
                </label>
              </div>
            </div>

            <div className="border-t border-zinc-800 pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-amber-300">Filtros de fila</Label>
                <Button size="sm" variant="ghost" onClick={addFiltro}
                        data-testid="perfil-import-add-filter"
                        className="text-amber-400 hover:text-amber-300 h-7">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Añadir filtro
                </Button>
              </div>
              {(editing.filtros || []).length === 0 && (
                <div className="text-xs text-zinc-500 italic">
                  Sin filtros: se importan todas las filas con nombre detectado.
                </div>
              )}
              {(editing.filtros || []).map((f, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end bg-zinc-900/40 p-2 rounded">
                  <div className="col-span-3">
                    <Label className="text-[10px] text-zinc-500">Columna</Label>
                    <Input value={f.columna} onChange={(e) => setFiltro(idx, { columna: e.target.value })}
                      className="bg-zinc-950 border-zinc-700 mt-0.5 text-xs h-8"
                      placeholder="estado, observaciones…" />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px] text-zinc-500">Operador</Label>
                    <Select value={f.operador} onValueChange={(v) => setFiltro(idx, { operador: v })}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-700 mt-0.5 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                        <SelectItem value="not_contains_any">NO contiene ninguno de</SelectItem>
                        <SelectItem value="contains_any">Contiene alguno de</SelectItem>
                        <SelectItem value="not_equals_any">NO es igual a ninguno</SelectItem>
                        <SelectItem value="equals_any">Es igual a alguno</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-5">
                    <Label className="text-[10px] text-zinc-500">Valores (separados por coma)</Label>
                    <Input value={(f.valores || []).join(", ")}
                      onChange={(e) => setFiltro(idx, { valores: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                      className="bg-zinc-950 border-zinc-700 mt-0.5 text-xs h-8"
                      placeholder="completado, hecho, ok" />
                  </div>
                  <div className="col-span-1 text-right">
                    <Button size="sm" variant="ghost" onClick={() => removeFiltro(idx)}
                      className="text-rose-400 hover:text-rose-300 h-8 px-2">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="text-[11px] text-zinc-500 mt-1">
                Ej.: «estado» + «NO contiene» + «completado, hecho» → omite filas ya terminadas.
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {editing ? (
            <>
              <Button variant="outline" onClick={() => setEditing(null)} className="bg-transparent border-zinc-700">
                Cancelar
              </Button>
              <Button onClick={save} disabled={loading}
                data-testid="perfil-import-save"
                className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</>
                         : (editing.id ? "Guardar cambios" : "Crear perfil")}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-zinc-700">
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
